import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { listBlocks, listProjects, listWards, listZones } from "@/lib/api";
import type { Project, ScopeGrant, ScopeLevel, SurveyBlock, Ward, Zone } from "@/lib/types";
import { Button, Spinner } from "./ui";
import { useLanguage } from "@/i18n";
import type { Translations } from "@/i18n/en";

/** A node in the picker, flattened to one shape so the row renderer is one
 *  component rather than four near-identical ones. */
type Node = {
  level: ScopeLevel;
  id: number;
  label: string;
  /** Ancestors, outermost first — used to decide whether a grant above this
   *  node already covers it. */
  path: { level: ScopeLevel; id: number }[];
};

const INDENT: Record<ScopeLevel, string> = {
  project: "pl-0",
  zone: "pl-4",
  ward: "pl-8",
  block: "pl-12",
};

const LEVEL_LABEL: Record<ScopeLevel, string> = {
  project: "Project",
  zone: "Zone",
  ward: "Ward",
  block: "Block",
};

function keyOf(level: ScopeLevel, id: number) {
  return `${level}:${id}`;
}

/**
 * Assigning an account to part of the survey hierarchy.
 *
 * Replaces a flat list of project checkboxes. That could say "this surveyor
 * works on the Theragoan campaign" but not "…and only in ward 3", which on a
 * campaign of 3000 properties is the difference between a work assignment and
 * a list of everything the office holds.
 *
 * **A tick covers everything beneath it.** Ticking a ward assigns its blocks,
 * including blocks imported next week — the backend resolves grants at read
 * time, so this is not a snapshot. Descendants of a ticked node are therefore
 * shown as covered and cannot be ticked separately: an extra grant under one
 * that already covers it means nothing, and offering it would imply the two
 * could differ.
 *
 * Children load only when a node is expanded. A municipality has thousands of
 * blocks and almost every assignment touches a handful, so fetching the whole
 * tree up front would be slow for a form that is mostly not looked at.
 */
export default function ScopeTree({
  initial,
  disabled,
  onCancel,
  onSubmit,
}: {
  initial: ScopeGrant[];
  disabled?: boolean;
  onCancel: () => void;
  onSubmit: (scopes: ScopeGrant[]) => void;
}) {
  const { t } = useLanguage();
  const [granted, setGranted] = useState<Set<string>>(
    () => new Set(initial.map((g) => keyOf(g.level, g.ref_id)))
  );

  /**
   * Nodes that merely CONTAIN a grant, keyed the same way.
   *
   * Ticking one block has to light up its ward and zone, or an assignment made
   * three levels down is invisible from the top of the tree and the operator
   * has to go hunting for it. Shown as a partial mark, never a full tick: a
   * full tick here would read as "the ward is assigned", and ticking the ward
   * really would hand over its other fifteen blocks.
   *
   * Seeded from the ancestry the server sends with each grant, because the tree
   * loads lazily and cannot otherwise know where an unexpanded block sits.
   */
  const [ancestorsOfGrants, setAncestorsOfGrants] = useState<Set<string>>(() => {
    const s = new Set<string>();
    for (const g of initial) {
      if (g.project_id != null && g.level !== "project") s.add(keyOf("project", g.project_id));
      if (g.zone_id != null && g.level !== "zone") s.add(keyOf("zone", g.zone_id));
      if (g.ward_id != null && g.level !== "ward") s.add(keyOf("ward", g.ward_id));
    }
    return s;
  });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [children, setChildren] = useState<Record<string, Node[]>>({});
  const [loading, setLoading] = useState<Set<string>>(new Set());
  const [roots, setRoots] = useState<Node[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listProjects()
      .then((ps: Project[]) =>
        setRoots(
          ps.map((p) => ({
            level: "project" as const,
            id: p.id,
            label: p.name,
            path: [],
          }))
        )
      )
      .catch((e) => {
        setRoots([]);
        setError(e instanceof Error ? e.message : t.scopeTree.couldntLoadProjects);
      });
  }, []);

  /** True when a grant on some ancestor already covers this node. */
  const coveredByAncestor = useCallback(
    (node: Node) => node.path.some((a) => granted.has(keyOf(a.level, a.id))),
    [granted]
  );

  /** True when this node is not granted itself but something inside it is. */
  const containsGrant = useCallback(
    (node: Node) => ancestorsOfGrants.has(keyOf(node.level, node.id)),
    [ancestorsOfGrants]
  );

  /** Re-derives the partial marks from every node we have loaded.
   *
   *  Merged with the server-seeded set rather than replacing it: expanding a
   *  ward tells us about its blocks, but says nothing about a grant in a branch
   *  still collapsed, and dropping those marks would make them flicker away as
   *  the operator explores. */
  const markAncestors = useCallback(
    (grantedNow: Set<string>) => {
      setAncestorsOfGrants((prev) => {
        const next = new Set(prev);
        for (const node of Object.values(children).flat()) {
          if (!grantedNow.has(keyOf(node.level, node.id))) continue;
          for (const a of node.path) next.add(keyOf(a.level, a.id));
        }
        // A node that is granted outright is not "partial".
        for (const k of [...next]) if (grantedNow.has(k)) next.delete(k);
        return next;
      });
    },
    [children]
  );

  const loadChildren = useCallback(
    async (node: Node) => {
      const k = keyOf(node.level, node.id);
      if (children[k] || node.level === "block") return;
      setLoading((s) => new Set(s).add(k));
      const path = [...node.path, { level: node.level, id: node.id }];
      try {
        let next: Node[] = [];
        if (node.level === "project") {
          next = (await listZones(node.id)).map((z: Zone) => ({
            level: "zone" as const,
            id: z.id,
            label: z.name,
            path,
          }));
        } else if (node.level === "zone") {
          next = (await listWards(node.id)).map((w: Ward) => ({
            level: "ward" as const,
            id: w.id,
            label: w.name,
            path,
          }));
        } else if (node.level === "ward") {
          next = (await listBlocks(node.id)).map((b: SurveyBlock) => ({
            level: "block" as const,
            id: b.id,
            label: b.name,
            path,
          }));
        }
        setChildren((c) => ({ ...c, [k]: next }));
      } catch (e) {
        setError(e instanceof Error ? e.message : t.scopeTree.couldntLoadLevel);
      } finally {
        setLoading((s) => {
          const n = new Set(s);
          n.delete(k);
          return n;
        });
      }
    },
    [children]
  );

  function toggleExpand(node: Node) {
    const k = keyOf(node.level, node.id);
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else {
        n.add(k);
        void loadChildren(node);
      }
      return n;
    });
  }

  function toggleGrant(node: Node) {
    const k = keyOf(node.level, node.id);
    setGranted((s) => {
      const n = new Set(s);
      if (n.has(k)) {
        n.delete(k);
        markAncestors(n);
        return n;
      }
      n.add(k);
      // Drop grants this one now covers. Keeping them would be harmless to the
      // backend but misleading here: unticking the parent later would silently
      // leave the child still assigned, which is not what the operator saw.
      for (const other of [...n]) {
        if (other === k) continue;
        const covered = Object.values(children)
          .flat()
          .find((c) => keyOf(c.level, c.id) === other);
        if (covered && covered.path.some((a) => keyOf(a.level, a.id) === k)) {
          n.delete(other);
        }
      }
      markAncestors(n);
      return n;
    });
  }

  function renderNode(node: Node): React.ReactNode {
    const k = keyOf(node.level, node.id);
    const isGranted = granted.has(k);
    const inherited = !isGranted && coveredByAncestor(node);
    const partial = !isGranted && !inherited && containsGrant(node);
    const isOpen = expanded.has(k);
    const kids = children[k];

    return (
      <div key={k}>
        <div
          className={clsx(
            "flex items-center gap-2 rounded-md py-1.5 pr-2.5 text-xs transition-colors",
            INDENT[node.level],
            (isGranted || inherited) && "bg-accent-subtle font-medium text-ink",
            partial && "bg-accent-subtle/50 text-ink",
            !isGranted && !inherited && !partial && "hover:bg-surface-sunken/60 text-ink-secondary"
          )}
        >
          {node.level !== "block" ? (
            <button
              type="button"
              onClick={() => toggleExpand(node)}
              className="flex h-6 w-6 flex-none items-center justify-center rounded text-ink-muted hover:bg-surface-raised hover:text-ink transition-colors"
              aria-label={isOpen ? t.scopeTree.collapse : t.scopeTree.expand}
            >
              {loading.has(k) ? (
                <Spinner className="h-3.5 w-3.5" />
              ) : isOpen ? (
                <ChevronDown className="h-5 w-5 text-ink-secondary" />
              ) : (
                <ChevronRight className="h-5 w-5 text-ink-secondary" />
              )}
            </button>
          ) : (
            <span className="h-6 w-6 flex-none" />
          )}

          <input
            type="checkbox"
            className="h-4 w-4 rounded flex-none accent-accent-solid cursor-pointer"
            checked={isGranted || inherited}
            // Indeterminate: something inside is assigned, this level is not.
            // Set through a ref because it is a DOM property with no HTML
            // attribute -- React cannot express it as a prop.
            ref={(el) => {
              if (el) el.indeterminate = partial;
            }}
            // Covered by an ancestor: the tick is real, but changing it here
            // would be a no-op, so it is shown rather than offered.
            disabled={disabled || inherited}
            title={
              inherited
                ? t.scopeTree.alreadyCovered
                : partial
                  ? t.scopeTree.partialHint
                  : undefined
            }
            onChange={() => toggleGrant(node)}
          />

          <span className={clsx("truncate select-none cursor-pointer", inherited && "text-ink-secondary")}>
            {node.label}
          </span>
          {partial && (
            <span className="flex-none rounded bg-accent-subtle px-1.5 py-0.5 text-2xs font-semibold text-accent-solid">
              {t.scopeTree.partial}
            </span>
          )}
          <span className="ml-auto flex-none rounded bg-surface-sunken px-1.5 py-0.5 text-2xs text-ink-muted">
            {LEVEL_LABEL[node.level]}
          </span>
        </div>

        {isOpen && kids && kids.length === 0 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={clsx("py-1 text-2xs text-ink-muted", INDENT[node.level])}
          >
            {t.scopeTree.nothingImportedYet}
          </motion.p>
        )}
        <AnimatePresence initial={false}>
          {isOpen && kids && kids.length > 0 && (
            <motion.div
              key={k + "-children"}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1.0] }}
              style={{ overflow: "hidden" }}
            >
              {kids.map(renderNode)}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  const scopes: ScopeGrant[] = [...granted].map((k) => {
    const [level, id] = k.split(":");
    return { level: level as ScopeLevel, ref_id: Number(id) };
  });

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-surface-sunken p-3">
      <p className="text-xs text-ink-secondary">
        {t.scopeTree.intro}
      </p>

      {error && (
        <p className="text-xs font-medium text-status-text-critical">{error}</p>
      )}

      <div className="thin-scroll max-h-[52vh] min-h-[200px] overflow-y-auto rounded-md bg-surface p-2 border border-line shadow-inner">
        {roots === null ? (
          <p className="flex items-center gap-2 p-3 text-xs text-ink-muted">
            <Spinner className="h-4 w-4" /> {t.scopeTree.loadingHierarchy}
          </p>
        ) : roots.length === 0 ? (
          <p className="p-3 text-xs text-ink-muted">{t.scopeTree.noProjectsToAssign}</p>
        ) : (
          <div className="space-y-0.5">{roots.map(renderNode)}</div>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-2xs text-ink-muted">
          {scopes.length === 0
            ? t.scopeTree.nothingAssigned
            : t.scopeTree.assignmentsCount(scopes.length)}
        </span>
        <div className="flex items-center gap-1.5">
          <Button size="sm" onClick={onCancel} disabled={disabled}>
            {t.scopeTree.cancel}
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={disabled}
            onClick={() => onSubmit(scopes)}
          >
            {t.scopeTree.save}
          </Button>
        </div>
      </div>
    </div>
  );
}
